import React, { useState, useEffect, useRef, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  FaHeart, FaComment, FaShare, FaPlay, FaPause,
  FaVolumeUp, FaVolumeMute, FaPlus, FaTimes,
  FaMusic, FaEye, FaChevronUp, FaChevronDown
} from 'react-icons/fa';
import api from '../utils/api';
import { toast } from 'sonner';
import Sidebar from '../components/Sidebar';

const SAMPLE_CLIPS = [
  {
    id: 'sample-1', user_id: 'demo', username: 'creator_one', full_name: 'Alex Creative',
    avatar_url: 'https://images.pexels.com/photos/220453/pexels-photo-220453.jpeg',
    video_url: 'https://commondatastorage.googleapis.com/gtv-videos-bucket/sample/ForBiggerBlazes.mp4',
    caption: 'Quick tips for better content creation! #pointclips #create', duration: 15,
    hashtags: ['pointclips', 'create'], audio_name: 'Original Audio',
    likes_count: 842, comments_count: 56, shares_count: 23, views_count: 4200,
    created_at: new Date().toISOString(),
  },
  {
    id: 'sample-2', user_id: 'demo2', username: 'travel_vibes', full_name: 'Jordan Travel',
    avatar_url: 'https://images.pexels.com/photos/1043471/pexels-photo-1043471.jpeg',
    video_url: 'https://commondatastorage.googleapis.com/gtv-videos-bucket/sample/ForBiggerEscapes.mp4',
    caption: 'This view was insane... point to share yours! #travel #pointclips', duration: 30,
    hashtags: ['travel', 'pointclips'], audio_name: 'Sunset Beats',
    likes_count: 2100, comments_count: 134, shares_count: 89, views_count: 12400,
    created_at: new Date().toISOString(),
  },
  {
    id: 'sample-3', user_id: 'demo3', username: 'tech_guru', full_name: 'Sam Tech',
    avatar_url: 'https://images.pexels.com/photos/2379004/pexels-photo-2379004.jpeg',
    video_url: 'https://commondatastorage.googleapis.com/gtv-videos-bucket/sample/ForBiggerFun.mp4',
    caption: 'Wait for it... mind blown! #tech #coding #pointclips', duration: 60,
    hashtags: ['tech', 'coding', 'pointclips'], audio_name: 'Tech Beats',
    likes_count: 5600, comments_count: 312, shares_count: 178, views_count: 34000,
    created_at: new Date().toISOString(),
  },
];

const formatCount = (n) => {
  if (n >= 1000000) return (n / 1000000).toFixed(1) + 'M';
  if (n >= 1000) return (n / 1000).toFixed(1) + 'K';
  return String(n);
};

// ─── Create Clip Modal ───
const CreateClipModal = ({ show, onClose, onCreated }) => {
  const [videoUrl, setVideoUrl] = useState('');
  const [caption, setCaption] = useState('');
  const [duration, setDuration] = useState(30);
  const [submitting, setSubmitting] = useState(false);

  if (!show) return null;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!videoUrl.trim()) { toast.error('Please enter a video URL'); return; }
    setSubmitting(true);
    try {
      const hashtags = caption.match(/#\w+/g)?.map(h => h.slice(1)) || [];
      const res = await api.post('/clips', { video_url: videoUrl, caption, duration, hashtags });
      toast.success('PointClip published!');
      onCreated(res.data);
      onClose();
      setVideoUrl(''); setCaption(''); setDuration(30);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create clip');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4" data-testid="create-clip-modal">
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        className="bg-[#161B22] rounded-2xl w-full max-w-md border border-white/10 shadow-2xl"
      >
        <div className="p-5 border-b border-white/5 flex items-center justify-between">
          <h2 className="text-white font-outfit font-bold text-lg font-arthaus">Create PointClip</h2>
          <button onClick={onClose} className="text-white/40 hover:text-white p-1" data-testid="close-create-clip">
            <FaTimes size={16} />
          </button>
        </div>
        <form onSubmit={handleSubmit} className="p-5 space-y-4">
          <div>
            <label className="block text-white/50 text-xs font-bold uppercase tracking-wider mb-2">Video URL *</label>
            <input
              type="url" value={videoUrl} onChange={(e) => setVideoUrl(e.target.value)}
              placeholder="https://example.com/video.mp4"
              className="w-full bg-[#0B0F14] border border-white/10 text-white rounded-xl px-4 py-3 text-sm focus:outline-none focus:border-[#23cda0] placeholder:text-white/20"
              data-testid="clip-video-url"
            />
          </div>
          <div>
            <label className="block text-white/50 text-xs font-bold uppercase tracking-wider mb-2">Caption</label>
            <textarea
              value={caption} onChange={(e) => setCaption(e.target.value)}
              placeholder="Write a caption... use #hashtags"
              className="w-full bg-[#0B0F14] border border-white/10 text-white rounded-xl px-4 py-3 text-sm focus:outline-none focus:border-[#23cda0] placeholder:text-white/20 resize-none"
              rows={3} maxLength={300}
              data-testid="clip-caption"
            />
            <p className="text-white/20 text-xs text-right mt-1">{caption.length}/300</p>
          </div>
          <div>
            <label className="block text-white/50 text-xs font-bold uppercase tracking-wider mb-2">Duration</label>
            <div className="flex space-x-2" data-testid="clip-duration-picker">
              {[15, 30, 60].map(d => (
                <button
                  key={d} type="button" onClick={() => setDuration(d)}
                  data-testid={`duration-${d}`}
                  className={`flex-1 py-2 rounded-xl text-sm font-medium transition-all ${
                    duration === d
                      ? 'bg-gradient-to-r from-[#23cda0] to-[#2263f2] text-white'
                      : 'bg-[#0B0F14] text-white/40 border border-white/10 hover:border-white/20'
                  }`}
                >
                  {d}s
                </button>
              ))}
            </div>
          </div>
          <button
            type="submit" disabled={!videoUrl.trim() || submitting}
            data-testid="publish-clip-btn"
            className="w-full py-3 rounded-xl font-bold text-sm transition-all bg-gradient-to-r from-[#23cda0] to-[#2263f2] text-white hover:from-[#1db890] hover:to-[#1b56d9] disabled:opacity-40 disabled:cursor-not-allowed"
          >
            {submitting ? 'Publishing...' : 'Publish PointClip'}
          </button>
        </form>
      </motion.div>
    </div>
  );
};

// ─── Comments Drawer ───
const CommentsDrawer = ({ show, clip, onClose }) => {
  const [comments, setComments] = useState([]);
  const [newComment, setNewComment] = useState('');

  useEffect(() => {
    if (show && clip) {
      api.get(`/clips/${clip.id}`).then(res => setComments(res.data.comments || [])).catch(err => console.error('Failed to load clip comments', err));
    }
  }, [show, clip]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSend = async (e) => {
    e.preventDefault();
    if (!newComment.trim()) return;
    try {
      const res = await api.post(`/clips/${clip.id}/comment`, { content: newComment });
      setComments(prev => [...prev, res.data]);
      setNewComment('');
    } catch (err) { console.error('Failed to send clip comment', err); }
  };

  if (!show) return null;

  return (
    <motion.div
      initial={{ y: '100%' }} animate={{ y: 0 }} exit={{ y: '100%' }}
      className="fixed bottom-0 right-0 w-80 h-[60vh] bg-[#161B22] border-l border-t border-white/10 rounded-tl-2xl z-40 flex flex-col"
      data-testid="comments-drawer"
    >
      <div className="p-4 border-b border-white/5 flex items-center justify-between">
        <span className="text-white font-bold text-sm">{comments.length} Comments</span>
        <button onClick={onClose} className="text-white/40 hover:text-white" data-testid="close-comments"><FaTimes size={14} /></button>
      </div>
      <div className="flex-1 overflow-y-auto p-3 space-y-3">
        {comments.map(c => (
          <div key={c.id} className="flex items-start space-x-2">
            <img src={c.avatar_url || `https://ui-avatars.com/api/?name=${c.username}&background=161B22&color=23cda0`} alt="" className="w-7 h-7 rounded-full" />
            <div>
              <span className="text-[#23cda0] font-bold text-xs">{c.username}</span>
              <p className="text-white/80 text-sm">{c.content}</p>
            </div>
          </div>
        ))}
        {comments.length === 0 && <p className="text-white/20 text-sm text-center py-8">No comments yet</p>}
      </div>
      <form onSubmit={handleSend} className="p-3 border-t border-white/5 flex space-x-2">
        <input
          value={newComment} onChange={(e) => setNewComment(e.target.value)}
          placeholder="Add a comment..." data-testid="clip-comment-input"
          className="flex-1 bg-[#0B0F14] text-white text-sm rounded-xl px-3 py-2 focus:outline-none border border-white/10 placeholder:text-white/20"
        />
        <button type="submit" disabled={!newComment.trim()} data-testid="send-clip-comment"
          className="bg-gradient-to-r from-[#23cda0] to-[#2263f2] text-white p-2 rounded-xl disabled:opacity-30">
          <FaComment size={12} />
        </button>
      </form>
    </motion.div>
  );
};

// ─── Single Clip Card ───
const ClipCard = ({ clip, isActive, isMuted, onToggleMute, onLike, onComment, onShare }) => {
  const videoRef = useRef(null);
  const [isPlaying, setIsPlaying] = useState(true);
  const [liked, setLiked] = useState(false);
  const [likeCount, setLikeCount] = useState(clip.likes_count);
  const [showDoubleTapHeart, setShowDoubleTapHeart] = useState(false);
  const lastTap = useRef(0);

  useEffect(() => {
    if (!videoRef.current) return;
    if (isActive) {
      videoRef.current.currentTime = 0;
      videoRef.current.play().catch(() => {});
      setIsPlaying(true);
      // Record view
      if (!clip.id.startsWith('sample-')) {
        api.post(`/clips/${clip.id}/view`).catch(() => {});
      }
    } else {
      videoRef.current.pause();
    }
  }, [isActive, clip.id]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (videoRef.current) videoRef.current.muted = isMuted;
  }, [isMuted]); // eslint-disable-line react-hooks/exhaustive-deps

  const togglePlay = () => {
    if (!videoRef.current) return;
    if (isPlaying) { videoRef.current.pause(); } else { videoRef.current.play(); }
    setIsPlaying(!isPlaying);
  };

  const handleDoubleTap = () => {
    const now = Date.now();
    if (now - lastTap.current < 300) {
      handleLike();
      setShowDoubleTapHeart(true);
      setTimeout(() => setShowDoubleTapHeart(false), 800);
    }
    lastTap.current = now;
  };

  const handleLike = async () => {
    if (clip.id.startsWith('sample-')) {
      setLiked(!liked);
      setLikeCount(prev => liked ? prev - 1 : prev + 1);
      return;
    }
    try {
      const res = await api.post(`/clips/${clip.id}/like`);
      setLiked(res.data.liked);
      setLikeCount(res.data.likes_count);
    } catch (err) { console.error('Failed to like clip', err); }
  };

  return (
    <div className="relative w-full h-full bg-black snap-start" data-testid={`clip-${clip.id}`}>
      {/* Video */}
      <video
        ref={videoRef}
        src={clip.video_url}
        className="absolute inset-0 w-full h-full object-cover"
        loop playsInline muted={isMuted}
        onClick={handleDoubleTap}
      />

      {/* Play/pause overlay */}
      <AnimatePresence>
        {!isPlaying && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="absolute inset-0 flex items-center justify-center bg-black/20 pointer-events-none">
            <FaPlay className="text-white/60 text-5xl" />
          </motion.div>
        )}
      </AnimatePresence>

      {/* Double-tap heart */}
      <AnimatePresence>
        {showDoubleTapHeart && (
          <motion.div initial={{ opacity: 1, scale: 0.5 }} animate={{ scale: 1.2 }} exit={{ opacity: 0, scale: 1.5 }}
            className="absolute inset-0 flex items-center justify-center pointer-events-none">
            <FaHeart className="text-red-500 text-7xl drop-shadow-lg" />
          </motion.div>
        )}
      </AnimatePresence>

      {/* Gradient overlays */}
      <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-black/30 pointer-events-none" />

      {/* Right-side actions */}
      <div className="absolute right-3 bottom-28 flex flex-col items-center space-y-5 z-10">
        {/* Avatar */}
        <div className="relative">
          <img src={clip.avatar_url || 'https://images.pexels.com/photos/220453/pexels-photo-220453.jpeg'}
            alt="" className="w-11 h-11 rounded-full border-2 border-white object-cover" />
          <div className="absolute -bottom-1.5 left-1/2 -translate-x-1/2 w-5 h-5 bg-gradient-to-r from-[#23cda0] to-[#2263f2] rounded-full flex items-center justify-center">
            <FaPlus className="text-white" size={8} />
          </div>
        </div>

        {/* Like */}
        <button onClick={handleLike} className="flex flex-col items-center" data-testid={`like-clip-${clip.id}`}>
          <div className={`p-2 rounded-full ${liked ? 'text-red-500' : 'text-white'} transition-colors`}>
            <FaHeart size={24} className={liked ? 'animate-bounce' : ''} />
          </div>
          <span className="text-white text-xs font-medium">{formatCount(likeCount)}</span>
        </button>

        {/* Comment */}
        <button onClick={onComment} className="flex flex-col items-center" data-testid={`comment-clip-${clip.id}`}>
          <div className="p-2 text-white"><FaComment size={22} /></div>
          <span className="text-white text-xs font-medium">{formatCount(clip.comments_count)}</span>
        </button>

        {/* Share */}
        <button onClick={onShare} className="flex flex-col items-center" data-testid={`share-clip-${clip.id}`}>
          <div className="p-2 text-white"><FaShare size={20} /></div>
          <span className="text-white text-xs font-medium">{formatCount(clip.shares_count)}</span>
        </button>

        {/* Views */}
        <div className="flex flex-col items-center">
          <div className="p-2 text-white/50"><FaEye size={18} /></div>
          <span className="text-white/50 text-xs">{formatCount(clip.views_count)}</span>
        </div>
      </div>

      {/* Bottom info */}
      <div className="absolute bottom-4 left-3 right-16 z-10">
        <p className="text-white font-bold text-sm" data-testid="clip-username">@{clip.username}</p>
        <p className="text-white/90 text-sm mt-1 leading-snug" data-testid="clip-caption">{clip.caption}</p>
        {clip.hashtags?.length > 0 && (
          <div className="flex flex-wrap gap-1 mt-1.5">
            {clip.hashtags.map(tag => (
              <span key={tag} className="text-[#23cda0] text-xs font-medium">#{tag}</span>
            ))}
          </div>
        )}
        {clip.audio_name && (
          <div className="flex items-center space-x-1.5 mt-2">
            <FaMusic className="text-white/60" size={10} />
            <marquee className="text-white/60 text-xs w-40">{clip.audio_name}</marquee>
          </div>
        )}
      </div>

      {/* Mute button top-right */}
      <button
        onClick={onToggleMute}
        className="absolute top-4 right-3 bg-black/40 backdrop-blur-sm p-2 rounded-full z-10"
        data-testid="clip-mute-toggle"
      >
        {isMuted ? <FaVolumeMute className="text-white" size={14} /> : <FaVolumeUp className="text-white" size={14} />}
      </button>
    </div>
  );
};

// ─── Main Page ───
const PointClips = () => {
  const [clips, setClips] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [loading, setLoading] = useState(true);
  const [isMuted, setIsMuted] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [showComments, setShowComments] = useState(false);
  const containerRef = useRef(null);

  useEffect(() => {
    loadClips();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const loadClips = async () => {
    try {
      const res = await api.get('/clips/feed');
      setClips(res.data.length > 0 ? res.data : SAMPLE_CLIPS);
    } catch (err) {
      console.error('Failed to load clips', err);
      setClips(SAMPLE_CLIPS);
    } finally {
      setLoading(false);
    }
  };

  const handleScroll = useCallback(() => {
    if (!containerRef.current) return;
    const { scrollTop, clientHeight } = containerRef.current;
    const newIndex = Math.round(scrollTop / clientHeight);
    if (newIndex !== currentIndex && newIndex >= 0 && newIndex < clips.length) {
      setCurrentIndex(newIndex);
    }
  }, [currentIndex, clips.length]); // eslint-disable-line react-hooks/exhaustive-deps

  const scrollTo = (direction) => {
    if (!containerRef.current) return;
    const nextIndex = direction === 'up' ? Math.max(0, currentIndex - 1) : Math.min(clips.length - 1, currentIndex + 1);
    containerRef.current.scrollTo({ top: nextIndex * containerRef.current.clientHeight, behavior: 'smooth' });
  };

  const handleClipCreated = (newClip) => {
    setClips(prev => [newClip, ...prev]);
    setCurrentIndex(0);
    containerRef.current?.scrollTo({ top: 0, behavior: 'smooth' });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0B0F14]">
        <div className="grid grid-cols-1 md:grid-cols-12">
          <Sidebar />
          <main className="col-span-12 md:col-span-10 flex items-center justify-center h-screen">
            <motion.div animate={{ scale: [1, 1.1, 1] }} transition={{ duration: 1.5, repeat: Infinity }}
              className="w-16 h-16 rounded-2xl bg-gradient-to-br from-[#23cda0] via-[#2263f2] to-[#6c30ad] flex items-center justify-center">
              <FaPlay className="text-white text-xl ml-1" />
            </motion.div>
          </main>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0B0F14]" data-testid="pointclips-page">
      <div className="grid grid-cols-1 md:grid-cols-12">
        <Sidebar />
        <main className="col-span-12 md:col-span-10 relative">
          <div className="h-screen flex flex-col">
            {/* Header */}
            <div className="absolute top-0 left-0 right-0 z-20 p-4 flex items-center justify-between bg-gradient-to-b from-black/60 to-transparent pointer-events-none">
              <h1 className="text-white font-outfit font-bold text-xl tracking-tight pointer-events-auto font-arthaus" data-testid="pointclips-title">
                PointClips
              </h1>
              <button
                onClick={() => setShowCreate(true)}
                data-testid="create-clip-btn"
                className="pointer-events-auto bg-gradient-to-r from-[#23cda0] to-[#2263f2] text-white px-4 py-2 rounded-xl text-sm font-bold flex items-center space-x-2 hover:from-[#1db890] hover:to-[#1b56d9] transition-all"
              >
                <FaPlus size={12} />
                <span>Create</span>
              </button>
            </div>

            {/* Clips Feed - Vertical Scroll */}
            <div
              ref={containerRef}
              onScroll={handleScroll}
              className="flex-1 overflow-y-scroll snap-y snap-mandatory scrollbar-hide"
              style={{ scrollSnapType: 'y mandatory' }}
              data-testid="clips-feed"
            >
              {clips.map((clip, index) => (
                <div key={clip.id} className="h-screen snap-start" style={{ scrollSnapAlign: 'start' }}>
                  <ClipCard
                    clip={clip}
                    isActive={index === currentIndex}
                    isMuted={isMuted}
                    onToggleMute={() => setIsMuted(!isMuted)}
                    onLike={() => {}}
                    onComment={() => setShowComments(true)}
                    onShare={() => toast.success('Link copied!')}
                  />
                </div>
              ))}
            </div>

            {/* Navigation arrows */}
            <div className="absolute right-3 top-1/2 -translate-y-1/2 flex flex-col space-y-2 z-20">
              {currentIndex > 0 && (
                <button onClick={() => scrollTo('up')} className="bg-black/40 backdrop-blur-sm p-2 rounded-full text-white/60 hover:text-white transition-colors" data-testid="scroll-up">
                  <FaChevronUp size={16} />
                </button>
              )}
              {currentIndex < clips.length - 1 && (
                <button onClick={() => scrollTo('down')} className="bg-black/40 backdrop-blur-sm p-2 rounded-full text-white/60 hover:text-white transition-colors" data-testid="scroll-down">
                  <FaChevronDown size={16} />
                </button>
              )}
            </div>

            {/* Clip counter */}
            <div className="absolute bottom-4 left-1/2 -translate-x-1/2 z-20 bg-black/40 backdrop-blur-sm px-3 py-1 rounded-full">
              <span className="text-white/60 text-xs font-mono">{currentIndex + 1} / {clips.length}</span>
            </div>
          </div>
        </main>
      </div>

      <CreateClipModal show={showCreate} onClose={() => setShowCreate(false)} onCreated={handleClipCreated} />

      <AnimatePresence>
        {showComments && clips[currentIndex] && (
          <CommentsDrawer show={showComments} clip={clips[currentIndex]} onClose={() => setShowComments(false)} />
        )}
      </AnimatePresence>
    </div>
  );
};

export default PointClips;
